// Downloads — the record of offline chart tile packs on the user's Scouts.
// Redesigned (Tony, 3 Sep 2026) from a bare table into a self-explaining
// page: coverage thumbnails, expiry countdowns, live progress, zoom levels
// in sailor's terms, and a status legend. Everything shown is already in
// the /users/me/downloads payload; the coverage thumbnails are single OSM
// slippy tiles with the pack's bounds drawn over them.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownTrayIcon,
  DeviceTabletIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MapIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  TrashIcon,
  PlusIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';
import type { DownloadHistoryItem, DownloadPackStatus } from '../../types/device';
import { DownloadDetailModal } from '../../components/downloads/DownloadDetailModal';
import clsx from 'clsx';

const STATUS_CONFIG: Record<DownloadPackStatus, { label: string; color: string; hint: string }> = {
  ready: { label: 'On device', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', hint: 'working offline now' },
  downloading: { label: 'Downloading', color: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20', hint: 'the Scout is fetching tiles' },
  pending: { label: 'Pending', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', hint: 'waiting for the Scout to start' },
  queued: { label: 'Queued', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', hint: 'behind another download' },
  expired: { label: 'Expired', color: 'bg-slate-500/10 text-slate-400 border-slate-500/20', hint: 'still usable, no longer updated' },
  failed: { label: 'Failed', color: 'bg-red-500/10 text-red-400 border-red-500/20', hint: 'tiles already fetched are kept' },
};

function formatBytes(bytes: number | null): string {
  if (bytes == null || bytes === 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatZoomRange(levels: number[]): string {
  if (!levels.length) return '—';
  const sorted = [...levels].sort((a, b) => a - b);
  return sorted.length === 1 ? `z${sorted[0]}` : `z${sorted[0]}–${sorted[sorted.length - 1]}`;
}

/** Zoom levels in sailor's terms: what detail does this pack actually hold? */
function zoomWords(levels: number[]): string {
  if (!levels.length) return '';
  const max = Math.max(...levels);
  const detail = max <= 10 ? 'ocean passage' : max <= 14 ? 'coastal' : max <= 16 ? 'harbour' : 'berth';
  const min = Math.min(...levels);
  const broad = min <= 8 ? 'passage' : min <= 12 ? 'coastal' : 'harbour';
  return broad === detail ? `${detail} detail` : `${broad} → ${detail} detail`;
}

const daysUntil = (iso: string) => Math.floor((new Date(iso).getTime() - Date.now()) / 86_400_000);

function ExpiryNote({ dl }: { dl: DownloadHistoryItem }) {
  if (dl.status !== 'ready' || !dl.expires_at) return null;
  const d = daysUntil(dl.expires_at);
  if (d < 0) return null;   // the status flips to expired server-side
  if (d <= 30) {
    return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full border bg-amber-500/10 text-amber-400 border-amber-500/20 whitespace-nowrap">expires in {d} d</span>;
  }
  return <span className="text-xs text-slate-500 whitespace-nowrap">expires {new Date(dl.expires_at).toLocaleDateString('en-NZ', { month: 'short', day: 'numeric' })}</span>;
}

// ---- coverage thumbnail: one OSM tile with the pack's bounds drawn on it ----

const VIEW_W = 132, VIEW_H = 92;
const mercX = (lon: number) => (lon + 180) / 360;
const mercY = (lat: number) => {
  const r = (Math.max(-85, Math.min(85, lat)) * Math.PI) / 180;
  return (1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2;
};

function CoverageThumb({ bounds }: { bounds: { minLat: number; maxLat: number; minLon: number; maxLon: number } | null }) {
  if (!bounds) {
    return (
      <div className="w-[132px] h-[92px] shrink-0 rounded-lg border border-slate-700 bg-slate-800/60 flex items-center justify-center">
        <MapIcon className="w-6 h-6 text-slate-600" />
      </div>
    );
  }
  const x1 = mercX(bounds.minLon), x2 = mercX(bounds.maxLon);
  const y1 = mercY(bounds.maxLat), y2 = mercY(bounds.minLat);   // y grows southward
  const spanX = Math.max(x2 - x1, 1e-6), spanY = Math.max(y2 - y1, 1e-6);
  // Choose the zoom where the bbox is ~100px wide in world pixels (256·2^z).
  const z = Math.max(1, Math.min(10, Math.floor(Math.log2(0.42 / Math.max(spanX, spanY)))));
  const scale = 256 * Math.pow(2, z);
  const cx = ((x1 + x2) / 2) * scale, cy = ((y1 + y2) / 2) * scale;
  const left = cx - VIEW_W / 2, top = cy - VIEW_H / 2;
  // Position tiles by their RAW index (so layout stays continuous across the
  // antimeridian) and wrap the index only when building the tile URL.
  const n = Math.pow(2, z);
  const tiles: Array<{ tx: number; ty: number }> = [];
  for (let tx = Math.floor(left / 256); tx <= Math.floor((left + VIEW_W) / 256); tx++) {
    for (let ty = Math.floor(top / 256); ty <= Math.floor((top + VIEW_H) / 256); ty++) {
      if (ty >= 0 && ty < n) tiles.push({ tx, ty });
    }
  }
  return (
    <div className="relative w-[132px] h-[92px] shrink-0 rounded-lg border border-slate-700 overflow-hidden bg-slate-800" aria-hidden>
      {tiles.map((t, i) => (
        <img key={i} src={`https://tile.openstreetmap.org/${z}/${((t.tx % n) + n) % n}/${t.ty}.png`} alt="" loading="lazy"
          className="absolute max-w-none brightness-[0.85]"
          style={{ width: 256, height: 256, left: Math.round(t.tx * 256 - left), top: Math.round(t.ty * 256 - top) }} />
      ))}
      <div className="absolute border-2 border-cyan-400 bg-cyan-400/15 rounded-[3px]"
        style={{
          left: Math.round(x1 * scale - left), top: Math.round(y1 * scale - top),
          width: Math.max(6, Math.round((x2 - x1) * scale)), height: Math.max(6, Math.round((y2 - y1) * scale)),
        }} />
    </div>
  );
}

// ---- page ----

export function DownloadsPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [deviceFilter, setDeviceFilter] = useState<string>('');
  const [q, setQ] = useState('');
  const [selectedDownload, setSelectedDownload] = useState<DownloadHistoryItem | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; value: string } | null>(null);
  const limit = 20;
  const qc = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  const refetchAll = () => { qc.invalidateQueries({ queryKey: ['downloads'] }); qc.invalidateQueries({ queryKey: ['downloads-stats'] }); };

  const again = useMutation({
    mutationFn: (id: string) => api.downloadRequestAgain(id),
    onSuccess: () => { refetchAll(); addToast({ type: 'success', title: 'Queued for the Scout', message: 'It downloads next time the Scout is online.' }); },
    onError: () => addToast({ type: 'error', title: 'Could not queue the download' }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.downloadRemove(id),
    onSuccess: () => { refetchAll(); addToast({ type: 'success', title: 'Removed from history' }); },
    onError: () => addToast({ type: 'error', title: 'Could not remove it' }),
  });
  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.downloadRenameArea(id, name),
    onSuccess: () => { setRenaming(null); refetchAll(); addToast({ type: 'success', title: 'Area renamed' }); },
    onError: () => addToast({ type: 'error', title: 'Could not rename', message: 'Catalog areas are named by Vortex.' }),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['downloads', page, statusFilter],
    queryFn: () => api.getMyDownloads({ page, limit, status: statusFilter || undefined }),
    refetchInterval: (query) => ((query.state.data?.data ?? []).some((d) => d.status === 'downloading' || d.status === 'pending' || d.status === 'queued') ? 15_000 : false),
  });
  // A wider, unfiltered window feeds the stat tiles so they don't change
  // with paging or filters. 100 covers realistic histories.
  const { data: statsData } = useQuery({
    queryKey: ['downloads-stats'],
    queryFn: () => api.getMyDownloads({ page: 1, limit: 100 }),
    staleTime: 60_000,
  });
  const { data: usage } = useQuery({ queryKey: ['usage-summary'], queryFn: () => api.getMyUsage(), staleTime: 60_000 });

  const downloads = data?.data ?? [];
  const pagination = data?.pagination ?? { page: 1, limit, total: 0, totalPages: 0 };

  const stats = useMemo(() => {
    const all = statsData?.data ?? [];
    const ready = all.filter((d) => d.status === 'ready');
    const devices = new Set(all.map((d) => d.device.id));
    return {
      readyCount: ready.length,
      deviceCount: devices.size,
      bytes: ready.reduce((a, d) => a + (d.size_bytes ?? 0), 0),
      tiles: ready.reduce((a, d) => a + d.tiles_downloaded, 0),
      expiringSoon: ready.filter((d) => d.expires_at && daysUntil(d.expires_at) >= 0 && daysUntil(d.expires_at) <= 30).length,
    };
  }, [statsData]);

  const deviceOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of statsData?.data ?? []) m.set(d.device.id, d.device.name || d.device.type);
    return [...m.entries()];
  }, [statsData]);

  const visible = downloads.filter((d) =>
    (!deviceFilter || d.device.id === deviceFilter) &&
    (!q.trim() || d.region.name.toLowerCase().includes(q.trim().toLowerCase())));

  return (
    <div className="max-w-6xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white">Offline charts on your Scouts</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-3xl">
            Every time you download an area in VortexNav, the chart tiles are packed and stored on that Scout so they
            work with no connection. This page is the record of those packs — what's aboard, how big it is, and when
            it needs refreshing.
          </p>
        </div>
        <Link to="/app" className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-700 text-sm font-semibold text-slate-300 hover:border-cyan-500/50 hover:text-white transition-colors">
          <PlusIcon className="w-4 h-4" /> Prepare a new area
        </Link>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="card !p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">On your devices</div>
          <div className="text-xl font-bold text-white tabular-nums">{stats.readyCount} pack{stats.readyCount === 1 ? '' : 's'}</div>
          <div className="text-xs text-slate-500">across {stats.deviceCount} Scout{stats.deviceCount === 1 ? '' : 's'}</div>
        </div>
        <div className="card !p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Offline chart size</div>
          <div className="text-xl font-bold text-white tabular-nums">{formatBytes(stats.bytes)}</div>
          <div className="text-xs text-slate-500">{stats.tiles.toLocaleString()} tiles</div>
        </div>
        <div className="card !p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Expiring within 30 days</div>
          <div className={clsx('text-xl font-bold tabular-nums', stats.expiringSoon ? 'text-amber-400' : 'text-white')}>{stats.expiringSoon}</div>
          <div className="text-xs text-slate-500">{stats.expiringSoon ? 'refresh before they lapse' : 'nothing lapsing soon'}</div>
        </div>
        {usage && usage.tokens_included > 0 && (
          <div className="card !p-4">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Tokens this period</div>
            <div className="text-xl font-bold text-white tabular-nums">
              {usage.tokens_used.toLocaleString()} <span className="text-sm font-medium text-slate-500">/ {(usage.tokens_included + usage.tokens_rolled_over).toLocaleString()}</span>
            </div>
            <div className="text-xs text-slate-500">downloads pause at zero</div>
          </div>
        )}
      </div>

      <div className="border-l-2 border-cyan-500/60 bg-cyan-500/5 rounded-r-lg px-4 py-2.5 text-sm text-slate-400">
        <b className="text-slate-300">How it works:</b> draw an area in VortexNav, pick the detail level, and the Scout
        downloads it as a pack. Packs <b className="text-slate-300">expire</b> so your charts never go quietly stale —
        refreshing one costs tokens like a new download of the same area.
      </div>

      {/* Filters */}
      <div className="card !py-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-2.5 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search areas…"
              className="w-full bg-slate-800 border border-slate-700 text-slate-300 text-sm rounded-lg pl-9 pr-3 py-1.5 focus:ring-cyan-500 focus:border-cyan-500 placeholder:text-slate-500" />
          </div>
          {deviceOptions.length > 1 && (
            <select value={deviceFilter} onChange={(e) => setDeviceFilter(e.target.value)} aria-label="Device"
              className="bg-slate-800 border border-slate-700 text-slate-300 text-sm rounded-lg px-3 py-1.5 focus:ring-cyan-500 focus:border-cyan-500">
              <option value="">All Scouts</option>
              {deviceOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          )}
          <FunnelIcon className="w-4 h-4 text-slate-500" />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            aria-label="Status"
            className="bg-slate-800 border border-slate-700 text-slate-300 text-sm rounded-lg px-3 py-1.5 focus:ring-cyan-500 focus:border-cyan-500"
          >
            <option value="">All statuses</option>
            {(Object.keys(STATUS_CONFIG) as DownloadPackStatus[]).map((s) => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
          </select>
          {pagination.total > 0 && (
            <span className="text-xs text-slate-500 ml-auto">{pagination.total} download{pagination.total !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>

      {/* Pack list */}
      <div className="card p-0 overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12 text-slate-500">Loading downloads...</div>
        ) : visible.length === 0 ? (
          <div className="text-center py-12 px-6">
            <ArrowDownTrayIcon className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 font-medium">{downloads.length ? 'Nothing matches the current filters.' : 'No offline charts yet'}</p>
            {!downloads.length && (
              <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                Open VortexNav on your Scout, draw the area you sail, and its tile pack will appear here as it downloads.
              </p>
            )}
          </div>
        ) : (
          <>
            <ul className="divide-y divide-slate-700/60">
              {visible.map((dl) => {
                const cfg = STATUS_CONFIG[dl.status] ?? STATUS_CONFIG.pending;
                const inFlight = dl.status === 'downloading' || dl.status === 'pending' || dl.status === 'queued';
                const pct = dl.tile_count ? Math.min(100, Math.round((dl.tiles_downloaded / dl.tile_count) * 100)) : dl.progress;
                return (
                  <li key={dl.id}>
                    <div className="flex items-center gap-4 px-4 py-3.5 hover:bg-slate-800/30 transition-colors">
                      <CoverageThumb bounds={dl.region.bounds} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          {renaming?.id === dl.id ? (
                            <form className="flex items-center gap-1.5" onSubmit={(e) => { e.preventDefault(); if (renaming.value.trim()) rename.mutate({ id: dl.id, name: renaming.value.trim() }); }}>
                              <input autoFocus value={renaming.value} onChange={(e) => setRenaming({ id: dl.id, value: e.target.value })} maxLength={120}
                                onKeyDown={(e) => { if (e.key === 'Escape') setRenaming(null); }}
                                className="bg-slate-800 border border-cyan-500/50 text-white text-sm rounded-lg px-2 py-1 w-64" aria-label="Area name" />
                              <button type="submit" disabled={rename.isPending} className="text-xs font-semibold text-cyan-400 hover:text-cyan-300">Save</button>
                              <button type="button" onClick={() => setRenaming(null)} className="text-xs text-slate-500 hover:text-slate-300">Cancel</button>
                            </form>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 min-w-0">
                              <span className="text-[15px] font-semibold text-white truncate">{dl.region.name}</span>
                              {dl.region.slug?.startsWith('custom-') && (
                                <button type="button" onClick={() => setRenaming({ id: dl.id, value: dl.region.name })} aria-label={`Rename ${dl.region.name}`}
                                  className="text-slate-600 hover:text-cyan-400"><PencilIcon className="w-3.5 h-3.5" /></button>
                              )}
                            </span>
                          )}
                          <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border', cfg.color)}>{cfg.label}</span>
                          <ExpiryNote dl={dl} />
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-slate-400">
                          <span className="inline-flex items-center gap-1.5"><DeviceTabletIcon className="w-4 h-4 text-slate-500" /><b className="text-slate-300 font-medium">{dl.device.name || dl.device.type}</b></span>
                          <span>Detail: <b className="text-slate-300 font-medium">{zoomWords(dl.zoom_levels)}</b> <span className="font-mono text-xs text-slate-500">({formatZoomRange(dl.zoom_levels)})</span></span>
                          <span className="tabular-nums">
                            <b className="text-slate-300 font-medium">{dl.tiles_downloaded.toLocaleString()}</b>
                            {dl.tile_count != null && dl.tiles_downloaded < dl.tile_count && <span> / {dl.tile_count.toLocaleString()}</span>} tiles
                            {dl.size_bytes != null && <> · <b className="text-slate-300 font-medium">{formatBytes(dl.size_bytes)}</b></>}
                          </span>
                          <span>{dl.downloaded_at
                            ? <>downloaded <b className="text-slate-300 font-medium">{new Date(dl.downloaded_at).toLocaleDateString('en-NZ', { month: 'short', day: 'numeric' })}</b></>
                            : <>requested <b className="text-slate-300 font-medium">{new Date(dl.requested_at).toLocaleDateString('en-NZ', { month: 'short', day: 'numeric' })}</b></>}</span>
                          {dl.status === 'failed' && <span className="text-red-400">{cfg.hint}</span>}
                          {dl.status === 'expired' && <span className="text-slate-500">{cfg.hint} — download the area again to refresh it</span>}
                        </div>
                        {inFlight && (
                          <div className="mt-2 max-w-md">
                            <div className="h-1.5 rounded-full bg-slate-700 overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-cyan-600 to-cyan-400 transition-all" style={{ width: `${pct}%` }} />
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5 tabular-nums">{pct}% · {cfg.hint}</div>
                          </div>
                        )}
                      </div>
                      <div className="shrink-0 flex flex-col items-stretch gap-1.5">
                        <button type="button" onClick={() => setSelectedDownload(dl)}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-700 text-xs font-semibold text-slate-300 hover:border-cyan-500/50 hover:text-white transition-colors">
                          <MapIcon className="w-4 h-4" /> View coverage
                        </button>
                        {dl.status === 'ready' && (
                          <button type="button" disabled={again.isPending} onClick={() => again.mutate(dl.id)}
                            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-700 text-xs font-semibold text-slate-300 hover:border-cyan-500/50 hover:text-white transition-colors disabled:opacity-40">
                            <ArrowPathIcon className="w-4 h-4" /> Refresh pack
                          </button>
                        )}
                        {(dl.status === 'failed' || dl.status === 'expired') && (
                          <>
                            <button type="button" disabled={again.isPending} onClick={() => again.mutate(dl.id)}
                              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-xs font-semibold text-white transition-colors disabled:opacity-40">
                              <ArrowPathIcon className="w-4 h-4" /> {dl.status === 'failed' ? 'Retry download' : 'Download again'}
                            </button>
                            <button type="button" disabled={remove.isPending} onClick={() => remove.mutate(dl.id)}
                              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-500/40 text-xs font-semibold text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40">
                              <TrashIcon className="w-4 h-4" /> Remove
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-slate-700">
                <span className="text-xs text-slate-500">Page {pagination.page} of {pagination.totalPages}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                    className="p-1 text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"><ChevronLeftIcon className="w-4 h-4" /></button>
                  <button onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))} disabled={page >= pagination.totalPages}
                    className="p-1 text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"><ChevronRightIcon className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Status legend */}
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
        {(Object.keys(STATUS_CONFIG) as DownloadPackStatus[]).map((s) => (
          <span key={s}><b className={clsx('font-semibold', STATUS_CONFIG[s].color.split(' ').find((c) => c.startsWith('text-')))}>{STATUS_CONFIG[s].label}</b> — {STATUS_CONFIG[s].hint}</span>
        ))}
      </div>

      {selectedDownload && (
        <DownloadDetailModal download={selectedDownload} onClose={() => setSelectedDownload(null)} />
      )}
    </div>
  );
}

export default DownloadsPage;
